import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Divider,
  IconButton,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  AutoMode as AutoIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';

const ThemeDemoPage = () => {
  const { isDarkMode, theme, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();

  const features = [
    {
      title: 'Otomatik Sistem Tema Algılama',
      description: 'Kullanıcının sistem temasını otomatik olarak algılar ve uygular.',
      icon: <AutoIcon />,
      color: '#4caf50'
    },
    {
      title: 'Local Storage Desteği',
      description: 'Tema tercihi tarayıcıda saklanır ve bir sonraki ziyarette hatırlanır.',
      icon: <CheckIcon />,
      color: '#2196f3'
    },
    {
      title: 'Smooth Geçişler',
      description: 'Tema değişiklikleri yumuşak animasyonlarla gerçekleşir.',
      icon: <TrendingIcon />,
      color: '#ff9800'
    },
    {
      title: 'CSS Değişkenleri',
      description: 'Tüm renkler CSS değişkenleri ile yönetilir, kolay özelleştirme.',
      icon: <PaletteIcon />,
      color: '#9c27b0'
    }
  ];

  const colorPalette = [
    { name: 'Primary', light: '#1976d2', dark: '#64b5f6' },
    { name: 'Secondary', light: '#dc004e', dark: '#f48fb1' },
    { name: 'Success', light: '#4caf50', dark: '#66bb6a' },
    { name: 'Warning', light: '#ff9800', dark: '#ffb74d' },
    { name: 'Error', light: '#f44336', dark: '#ef5350' },
    { name: 'Info', light: '#2196f3', dark: '#42a5f5' }
  ];

  return (
    <Box sx={{ py: 8, bgcolor: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 800, 
              mb: 2,
              background: 'var(--gradient-primary)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'var(--text-primary)'
            }}
          >
            Tema Sistemi Demo
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'var(--text-secondary)',
              mb: 4,
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Light ve Dark mode özelliklerini keşfedin. Tema değişiklikleri anında uygulanır ve 
            tercihiniz tarayıcıda saklanır.
          </Typography>
          
          {/* Theme Toggle Buttons */}
          <Stack 
            direction="row" 
            spacing={2} 
            justifyContent="center" 
            alignItems="center"
            sx={{ mb: 4 }}
          >
            <ThemeToggle variant="button" />
            <ThemeToggle variant="switch" />
            <ThemeToggle variant="icon" />
          </Stack>

          {/* Current Theme Info */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              bgcolor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              borderRadius: 3,
              maxWidth: 400,
              mx: 'auto'
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              {isDarkMode ? <DarkIcon color="primary" /> : <LightIcon color="primary" />}
              <Box>
                <Typography variant="h6" sx={{ color: 'var(--text-primary)' }}>
                  {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                  Aktif tema: {theme}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  bgcolor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 'var(--shadow-medium)',
                    borderColor: feature.color
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      bgcolor: `${feature.color}20`,
                      color: feature.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      color: 'var(--text-primary)'
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'var(--text-secondary)',
                      lineHeight: 1.6
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Color Palette */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            bgcolor: 'var(--bg-card)',
            border: '1px solid var(--border-primary)',
            borderRadius: 3,
            mb: 6
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              color: 'var(--text-primary)'
            }}
          >
            Renk Paleti
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'var(--text-secondary)',
              mb: 4
            }}
          >
            Tema değişikliklerinde renkler otomatik olarak güncellenir.
          </Typography>
          
          <Grid container spacing={3}>
            {colorPalette.map((color, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid var(--border-primary)',
                    bgcolor: 'var(--bg-secondary)'
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 2,
                      color: 'var(--text-primary)'
                    }}
                  >
                    {color.name}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Box>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: color.light,
                          mb: 1
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        Light
                      </Typography>
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: color.dark,
                          mb: 1
                        }}
                      />
                      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        Dark
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Interactive Demo */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            bgcolor: 'var(--bg-card)',
            border: '1px solid var(--border-primary)',
            borderRadius: 3
          }}
        >
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              color: 'var(--text-primary)'
            }}
          >
            Etkileşimli Demo
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'var(--text-primary)'
                  }}
                >
                  Butonlar
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={2}>
                  <Button variant="contained" sx={{ bgcolor: 'var(--accent-primary)' }}>
                    Primary
                  </Button>
                  <Button variant="outlined" sx={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}>
                    Secondary
                  </Button>
                  <Button variant="text" sx={{ color: 'var(--accent-primary)' }}>
                    Text
                  </Button>
                </Stack>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'var(--text-primary)'
                  }}
                >
                  Chips
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  <Chip label="Success" color="success" />
                  <Chip label="Warning" color="warning" />
                  <Chip label="Error" color="error" />
                  <Chip label="Info" color="info" />
                </Stack>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'var(--text-primary)'
                  }}
                >
                  Typography
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="h1" sx={{ color: 'var(--text-primary)' }}>
                    H1 Heading
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'var(--text-primary)' }}>
                    H3 Heading
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'var(--text-secondary)' }}>
                    Body text with secondary color
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--text-tertiary)' }}>
                    Caption text with tertiary color
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3, borderColor: 'var(--border-primary)' }} />
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'var(--text-secondary)',
                mb: 2
              }}
            >
              Tema değiştirmek için yukarıdaki butonları kullanın
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={toggleTheme}
              startIcon={isDarkMode ? <LightIcon /> : <DarkIcon />}
              sx={{
                bgcolor: 'var(--gradient-primary)',
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 'var(--shadow-medium)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isDarkMode ? 'Light Mode\'a Geç' : 'Dark Mode\'a Geç'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ThemeDemoPage;
